import React, { useState } from 'react';
import { ArrowLeft, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../config/properties';

/**
 * User Profile Edit Component
 * Allows users to edit their own profile information (firstName, lastName, email)
 * Does not allow changing password or privileges
 */
const UserProfileEdit = ({ setActiveTab }) => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      setMessageType('error');
      setMessage(t('allFieldsAreRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setMessageType('error');
      setMessage(t('pleaseEnterAValidEmailAddress'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Error updating profile: ${response.status}`);
      }

      setMessageType('success');
      setMessage(t('profileUpdatedSuccessfully'));

      setTimeout(() => {
        setActiveTab(0); // Go back to dashboard
      }, 1500);
    } catch (error) {
      console.error('Profile update error:', error);
      setMessageType('error');
      setMessage(error.message || t('failedToUpdateProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveTab(0); // Go back to dashboard
  };

  return (
    <div className="profile-edit-container">
      {/* Back Button */}
      <button
        className="btn btn-outline-secondary mb-4 d-flex align-items-center gap-2"
        onClick={handleBack}
        type="button"
        aria-label="Go back to dashboard"
      >
        <ArrowLeft size={18} />
        {t('back')}
      </button>

      {/* Page Header */}
      <div className="mb-4">
        <h2 className="mb-2">{t('editProfile')}</h2>
        <p className="text-muted">{t('updateYourPersonalInformation')}</p>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`alert alert-${messageType === 'success' ? 'success' : 'danger'} d-flex align-items-center gap-2 mb-4`}>
          {messageType === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertCircle size={20} />
          )}
          <span>{message}</span>
        </div>
      )}

      {/* Profile Edit Form */}
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Current Username - Read Only */}
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                {t('username')} <span className="text-muted">({t('readOnly')})</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="username"
                value={user?.username || ''}
                disabled
              />
            </div>

            {/* First Name */}
            <div className="mb-3">
              <label htmlFor="firstName" className="form-label">
                {t('firstName')} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={t('enterYourFirstName')}
                disabled={loading}
                required
              />
            </div>

            {/* Last Name */}
            <div className="mb-3">
              <label htmlFor="lastName" className="form-label">
                {t('lastName')} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={t('enterYourLastName')}
                disabled={loading}
                required
              />
            </div>

            {/* Email */}
            <div className="mb-4">
              <label htmlFor="email" className="form-label">
                {t('email')} <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t('enterYourEmailAddress')}
                disabled={loading}
                required
              />
              <small className="form-text text-muted">
                {t('weWillNeverShareYourEmailWithAnyoneElse')}
              </small>
            </div>

            {/* Info Box */}
            <div className="alert alert-info mb-4">
              <strong>{t('note')}</strong> {t('toChangeYourPasswordOrModifyAccessPrivilegesContactAnAdministrator')}
            </div>

            {/* Form Actions */}
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBack}
                disabled={loading}
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary d-flex align-items-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {t('saving')}...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {t('saveChanges')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Styling */}
      <style>{`
        .profile-edit-container {
          padding: 20px;
          max-width: 600px;
          margin: 0 auto;
        }

        .profile-edit-container .form-control:disabled {
          background-color: #f5f5f5;
          color: #6c757d;
        }

        .profile-edit-container .card {
          border: 1px solid #dee2e6;
        }

        .profile-edit-container .form-label {
          font-weight: 500;
          color: #212529;
          margin-bottom: 0.5rem;
        }

        .profile-edit-container .form-control {
          border-radius: 6px;
          border: 1px solid #dee2e6;
          transition: border-color 0.2s ease;
        }

        .profile-edit-container .form-control:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }

        .profile-edit-container .alert-info {
          background-color: #e7f3ff;
          border-color: #b3e5fc;
          color: #01579b;
          border-radius: 6px;
        }

        .profile-edit-container .spinner-border-sm {
          width: 1rem;
          height: 1rem;
          margin-right: 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default UserProfileEdit;
